import { Tile as TileModel, ImageKind } from "@blacknut/javascript-sdk/dist";
import { logE } from "@blacknut/logging/dist";
import { Focusable, useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import React, { useCallback } from "react";
import { ReactComponent as Home } from "../../assets/dist/ic_home.svg";
import { ReactComponent as PegiRestrictedOverlay } from "../../assets/dist/ic_pegi-restricted-overlay.svg";
import { useProfiler } from "../../hooks/useProfiler";
import { useTileSize } from "../../utils/TileWidthProvider";
import { LOGGING_TAG } from "../../utils/Utils";
import FadeInImage from "../FadeInImage/FadeInImage";
import ProfileIcon from "../ProfileIcon";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useAppNavigation } from "src/utils/Navigation/useAppNavigation";
import { useScrollContext } from "src/utils/scroll/ScrollProvider";

interface TileProps {
  className?: string;
  item: TileModel;
  index?: number;
}

const Tile = ({ className, item, index = 0 }: TileProps) => {
  const { navigate } = useAppNavigation();
  const onClick = useCallback(() => {
    if (item.game) {
      navigate(`/game/${item.game?.id}`);
    }
  }, [navigate, item.game]);
  const { active } = useSpatialNavigation();

  const isForbiddenSlot =
    (item.game && !item.game.name) || item.game?.userProfileRestriction;

  const clickable = !isForbiddenSlot;

  const image = item.game?.images?.find((img) => img.kind === ImageKind.GAME_HERO);
  if (!image && !isForbiddenSlot && item.game) {
    logE(LOGGING_TAG, "Invalid tile: %o", item);
  }

  const { bonusGameTileSize } = useTileSize();
  const { t } = useTranslation();
  const Root = useProfiler("tile");

  const { update: updateScrollContext } = useScrollContext();
  const onFocus = useCallback(
    (e) => {
      const selector = "." + [...e.srcElement.classList].join(".");
      updateScrollContext(selector);
    },
    [updateScrollContext],
  );

  return (
    <Root>
      <Focusable
        onPressEnter={onClick}
        className={clsx([
          // !WARNING! DO NOT use class "tile" for styling
          // It is purposely not scoped since it needs to be accessible from FocusableSection defaultElement prop
          "tile",
          styles.container,
          clickable && styles.clickable,
          active && styles.focusable,
          `tile_${index}`, // Combined with the "path" to have a unique selector to be focused
          className,
        ])}
        {...(clickable && { onClick })}
        data-testid="tile-bonus-game"
        style={{
          width: bonusGameTileSize.width,
        }}
        disabled={!clickable}
        onFocus={onFocus}
      >
        <div className={styles.imgContainer}>
          {item.game && !isForbiddenSlot && image && <FadeInImage image={image} />}

          {item.game && !isForbiddenSlot && !image && (
            <div className={styles.missing} data-testid="placeholder">
              <Home />
            </div>
          )}
          {isForbiddenSlot && image && (
            <>
              <div className={styles.forbiddenSlotOverlay}>
                <PegiRestrictedOverlay />
              </div>
              <FadeInImage className={clsx(styles.forbiddenSlot)} image={image} />
            </>
          )}
        </div>

        <div className={styles.descWrap}>
          {item.game && (
            <span>
              {t("bnPlus.nbDaysLeftTile", {
                count: item.game.addonOption?.bonusGameDaysLeft,
              })}
            </span>
          )}
          {!item.game && <span>{t("bnPlus.selectGame")}</span>}

          {item.game?.addonOption?.isBonusGame &&
            item.game.addonOption.bonusGameUser && (
              <ProfileIcon
                profile={item.game.addonOption.bonusGameUser}
                className={styles.avatar}
              />
            )}
        </div>
      </Focusable>
    </Root>
  );
};
export default React.memo(Tile);
